/**
 * Server mode: fighting and staying alive are the server's (server/actions/
 * combat.ts, itemUse.ts, survival.ts). This is the client half:
 *
 *  - `stats` is written into the page's PlayerStats, which the HUD already
 *    draws (hearts, food, air, XP, effects); nothing here decides damage,
 *  - `vel` is added to the local player's velocity, `death` is a chat line,
 *  - `fx` kinds of workstream C become sounds, particles, toasts, the bed,
 *  - `attack` / `use` / `release` / `respawn` / `wake` are sent as intents.
 *
 * main.ts keeps the local animation (swing, bow draw, eating) and calls
 * `tick()` once per game tick instead of PlayerStats.tick().
 */

import type { PlayerStats } from '../game/playerStats';
import type { PlayerTarget } from '../entities/host';
import { I } from '../game/itemIds';
import { itemDef } from '../game/itemdb';
import { isDrinkable } from '../game/brewing/use';
import { isEmpty, type GameMode, type Stack } from '../game/types';
import type { StatsFields } from '../server/protocol';
import type { ServerSession } from './serverSession';

export interface CombatDeps {
  srv: ServerSession;
  stats: PlayerStats;
  player: {
    position: { x: number; y: number; z: number };
    velocity: { x: number; y: number; z: number };
    onGround: boolean;
    dead: boolean;
    hurt(fromX?: number, fromZ?: number): void;
    startSwing(): void;
  };
  /** The page's entity manager: other players are put in `remotePlayers` so its ray test finds them. */
  entities: { remotePlayers: readonly PlayerTarget[] };
  /** Replicated entities of a kind (client/entities.ts `others`). */
  others(kind: string): { id: number; x: number; y: number; z: number }[];
  mode(): GameMode;
  setMode(m: GameMode): void;
  /** We died: close screens, show the death screen. Nothing is dropped here. */
  onDeath(): void;
  toast(text: string): void;
  chat(text: string): void;
  sound(id: string, x?: number, y?: number, z?: number): void;
  /** Crit stars (magic = the enchanted-hit sparkle) on a body at this point. */
  crit?(x: number, y: number, z: number, magic: boolean): void;
  /** An egg / a splash potion broke here. */
  eggBreak?(x: number, y: number, z: number): void;
  splash?(x: number, y: number, z: number, color: number, linger: boolean): void;
  /** A firework star burst in these colours. */
  fwBurst?(x: number, y: number, z: number, colours: readonly number[]): void;
  /** Lie down in the bed at this cell / get up. */
  enterBed(x: number, y: number, z: number): void;
  leaveBed(): void;
  itemName(id: number): string;
}

/** What a right click with this item starts: 'hold' while the button is down, 'done' at once, null = not an item use. */
export type UseStart = 'hold' | 'done' | null;

interface FxData { eid?: number; item?: number; x?: number; y?: number; z?: number; text?: string; rested?: boolean; power?: number; color?: number; linger?: boolean; riptide?: number; reel?: string; k?: string; kind?: string; sound?: string; colours?: number[] }

const HURT_SOUND: Record<string, string> = { drown: 'entity.player.hurt_drown', fire: 'entity.player.hurt_on_fire', lava: 'entity.player.hurt_on_fire' };

export class ClientCombat {
  /** A `use` was sent and not yet released. */
  private active = false;
  private first = true;
  private login: StatsFields | null = null;
  private targets: PlayerTarget[] = [];

  constructor(private readonly d: CombatDeps) {
    const srv = d.srv;
    // local rules must not change what the server owns
    d.stats.damage = () => 0;
    d.stats.addXp = () => { /* the server's */ };
    // the login's stats are applied by the first tick(): the page is still being built here
    this.login = srv.welcome?.stats ?? null;
    srv.on('welcome', (m) => { this.first = true; this.active = false; this.login = m.stats; });
    srv.on('stats', (m) => { this.login = null; this.apply(m); });
    srv.on('vel', (m) => {
      const v = d.player.velocity;
      if (m.kb) {
        v.x = v.x / 2 + m.x; v.z = v.z / 2 + m.z;
        if (d.player.onGround) v.y = Math.min(0.4, v.y / 2 + m.y);
      } else { v.x += m.x; v.y += m.y; v.z += m.z; }
    });
    srv.on('death', (m) => { d.chat(`${m.name} ${m.cause}`); });
    srv.on('fx', (m) => { this.fx(m.k, m.x, m.y, m.z, (m.d && typeof m.d === 'object' ? m.d : {}) as FxData); });
  }

  private get eid(): number { return this.d.srv.welcome?.eid ?? -1; }

  private apply(m: StatsFields): void {
    const d = this.d, s = d.stats;
    const before = s.health + s.absorption, wasDead = s.dead;
    const hurtTime = s.hurtTime, deathTime = s.deathTime;
    s.load({
      health: m.health, food: m.food, saturation: m.sat, exhaustion: 0, air: m.air,
      xpTotal: m.xpTotal, xpLevel: m.xpLevel, xpProgress: m.xpProgress, score: Math.max(s.score, m.xpTotal),
      fire: m.fire ? Math.max(20, s.fire) : 0, effects: m.fx, absorption: m.abs,
    });
    s.dead = m.dead;
    s.hurtTime = !this.first && m.health + m.abs < before ? 10 : hurtTime;
    s.deathTime = m.dead ? deathTime : 0;
    if (m.mode !== d.mode()) d.setMode(m.mode);
    if (m.dead && (!wasDead || this.first)) { this.active = false; d.onDeath(); }
    else if (!m.dead && wasDead) d.player.dead = false;
    this.first = false;
  }

  private fx(k: string, x: number, y: number, z: number, f: FxData): void {
    const d = this.d, mine = f.eid === this.eid;
    switch (k) {
      case 'hurt':
        if (!mine) return;
        d.player.hurt(f.x !== undefined && f.z !== undefined ? f.x - d.player.position.x : 0, f.x !== undefined && f.z !== undefined ? f.z - d.player.position.z : 0);
        d.sound(HURT_SOUND[f.kind ?? ''] ?? 'entity.player.hurt');
        return;
      case 'death': if (!mine) d.sound('entity.player.death', x, y, z); return;
      case 'totem':
        d.sound('item.totem.use', x, y, z);
        if (mine) d.toast('Totem of Undying!');
        return;
      case 'swing': return; // other players' arm swing is drawn from their record
      case 'crit': d.crit?.(x, y, z, false); d.sound('entity.player.attack.crit', x, y, z); return;
      case 'magic_crit': d.crit?.(x, y, z, true); return;
      case 'sweep': d.sound('entity.player.attack.sweep', x, y, z); return;
      case 'shield': d.sound('item.shield.block', x, y, z); return;
      case 'shield_break': d.sound('item.shield.break', x, y, z); return;
      case 'break':
        d.sound('entity.item.break', x, y, z);
        if (mine && f.item) d.toast(`Your ${d.itemName(f.item)} broke`);
        return;
      case 'bow': d.sound('entity.arrow.shoot', x, y, z); if (mine) d.player.startSwing(); return;
      case 'crossbow': d.sound('item.crossbow.shoot', x, y, z); return;
      case 'throw':
        if (mine) d.player.startSwing();
        if (f.item === I.TRIDENT) d.sound(f.riptide ? 'item.trident.riptide_1' : 'item.trident.throw', x, y, z);
        else if (f.item === I.FIREWORK_ROCKET) d.sound('entity.firework_rocket.launch', x, y, z);
        else if (f.item !== I.FISHING_ROD) d.sound('entity.arrow.shoot', x, y, z);
        return;
      case 'fw': // a firework rocket's sound and / or star burst
        if (f.sound) d.sound(f.sound, x, y, z);
        if (f.colours) d.fwBurst?.(x, y, z, f.colours);
        return;
      case 'eat': d.sound('entity.generic.eat', x, y, z); return;
      case 'burp': d.sound('entity.player.burp', x, y, z); return;
      case 'drink': if (!mine) d.sound('entity.generic.drink', x, y, z); return;
      case 'egg': d.eggBreak?.(x, y, z); return;
      case 'splash': d.sound('block.glass.break', x, y, z); d.splash?.(x, y, z, f.color ?? 0x385dc6, f.linger === true); return;
      case 'pearl': d.sound('entity.enderman.teleport', x, y, z); return;
      case 'bobber':
        if (f.k === 'splash' || f.k === 'bite') d.sound('entity.generic.splash', x, y, z);
        else if (f.k === 'cast') d.sound('entity.arrow.shoot', x, y, z);
        else if (f.k === 'catch') d.sound('entity.item.pickup', x, y, z);
        return;
      case 'sleep':
        if (mine && f.x !== undefined && f.y !== undefined && f.z !== undefined) d.enterBed(f.x, f.y, f.z);
        return;
      case 'wake':
        if (!mine) return;
        d.leaveBed();
        if (f.rested) d.toast('You slept until dawn');
        return;
      case 'msg': if (f.text) d.toast(f.text); return;
      default:
    }
  }

  /** Left click on a mob or a player (their entity id). */
  attack(id: number): void { this.d.srv.send({ t: 'attack', id }); }

  /** What a right click with `held` does on the server, without sending anything. */
  classify(held: Stack, gliding: boolean): UseStart {
    if (isEmpty(held)) return null;
    switch (held.id) {
      case I.CROSSBOW: return held.charged ? 'done' : 'hold';
      case I.BOW: case I.TRIDENT: return 'hold';
      case I.EGG: case I.ENDER_PEARL: case I.SPLASH_POTION: case I.LINGERING_POTION: case I.FISHING_ROD: return 'done';
      case I.FIREWORK_ROCKET: return gliding ? 'done' : null;
      case I.SHIELD: return null; // raised by holding the button (see tick)
      default:
    }
    const def = itemDef(held.id);
    if (def.food) return this.d.stats.food < 20 || def.food.alwaysEdible === true || this.d.mode() === 'creative' ? 'hold' : null;
    if (isDrinkable(held.id)) return 'hold';
    if (def.armor) return 'done';
    return null;
  }

  /** Right click with the held item: sends `use` when the server has a use for it. */
  use(held: Stack, gliding: boolean): UseStart {
    const k = this.classify(held, gliding);
    if (k) { this.d.srv.send({ t: 'use', seq: this.d.srv.nextSeq(), hand: 0 }); this.active = true; }
    return k;
  }

  respawn(): void { this.d.srv.send({ t: 'respawn' }); }
  wake(): void { this.d.srv.send({ t: 'wake' }); }

  /**
   * Once per game tick. `rightHeld`: the use button is down; `wantShield`: a
   * shield should be up (button or sneak, shield in a hand, nothing else in use);
   * `shieldInOffhand`: the main hand holds something else.
   */
  tick(rightHeld: boolean, wantShield: boolean, shieldInOffhand: boolean): void {
    const d = this.d, s = d.stats;
    if (this.login) { const m = this.login; this.login = null; this.apply(m); }
    if (this.active && !rightHeld && !wantShield) { this.active = false; d.srv.send({ t: 'release' }); }
    else if (!this.active && wantShield && !s.dead) { this.active = true; d.srv.send({ t: 'use', seq: d.srv.nextSeq(), hand: shieldInOffhand ? 1 : 0 }); }
    // HUD animation only: the numbers are the server's
    s.tickCount++;
    if (s.hurtTime > 0) s.hurtTime--;
    if (s.dead && s.deathTime < 20) s.deathTime++;
    for (const e of s.effects.list) if (e.dur > 1) e.dur--;
    // other players as attack targets for the page's ray test (EntityManager.raycastPlayer)
    const rows = d.others('player'), t = this.targets;
    t.length = 0;
    for (const r of rows) {
      if (r.id === this.eid) continue;
      t.push({
        id: String(r.id), eid: r.id, x: r.x, y: r.y, z: r.z, eyeY: r.y + 1.62, width: 0.6, height: 1.8,
        alive: true, creative: false, spectator: false, damage: () => undefined, knockback: () => undefined,
      });
    }
    d.entities.remotePlayers = t;
  }
}

export function installCombat(d: CombatDeps): ClientCombat { return new ClientCombat(d); }
